/**
 * The `wall` autotile kind: building walls (wood, stone, ...) as data.
 *
 * A wall definition says, for each autotile index a material can draw, which tiles to emit at which
 * offsets from the wall's cell (the ANCHOR) -- a lone wall is a cap one row up and a base on the
 * cell -- and, where the art depends on more than the index, which alternative to take. HouseBuilder
 * (house/feature/house.ts) asks `resolveWallCell` for every wall cell, in row-major order, and writes
 * what it returns through its own entry()/addFor(); Sprite Lab asks the same function.
 *
 * Every emitted tile is grouped under the anchor as its PARENT (HouseBuilder's `addFor(x, y)`): one
 * wall = one object, one HP counter, one coordinateCache key, one sort row. `parent: "self"` makes a
 * tile its own object instead (HouseBuilder's `add`).
 *
 * Two of HouseBuilder's behaviours shape what a rule means, and are kept by construction:
 *  - its entry() keeps ONE tile per cell+layer, so a second emit onto a cell re-points the tile that
 *    is already there (only its tile position changes; its tileset stays) rather than adding one;
 *  - `exists` sees only tiles emitted EARLIER -- by cells before this one in row-major order, by
 *    earlier steps of this cell's own rule, or by anything else HouseBuilder put on the layer first.
 *    So the order of a rule's steps is part of the data.
 *
 * Conditions read the index grid of EVERY wall cell, whatever its material: one house grid holds
 * all of them, so a wooden wall joins a stone one.
 *
 * The indices a definition has a rule for are the ones it can draw; any other index is a shape the
 * material cannot render, and placement refuses it (autotiler.ts, COMPUTABLE_WALL_AUTOTILE_INDICES).
 *
 * `itemType` links a material to the item that builds it (HouseBuilder.wallMaterials is derived from
 * it); content validation requires that item to exist with buildType "wall", and no two materials to
 * name the same item.
 *
 * A leaf like autotile_library.ts (which validates and loads these): no models, no mechanics, no
 * map builder, no file system. Only types are imported.
 */
import type { TileRef } from "./autotile_library"

// ---------------------------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------------------------

/** [dx, dy] from the wall's cell. Each is -1, 0 or 1: a wall's pieces stay in its neighbourhood. */
export type WallOffset = [number, number]

/** One tile, at an offset from the wall's cell, on the definition's layer and tileset. */
export interface WallEmit {
    dx?: number
    dy?: number
    /** Tile position in the definition's tileset. */
    tile: number
    /** Omitted: grouped under the wall's cell. "self": the tile is its own object. */
    parent?: "self"
}

export type WallCondition =
    /** There is a wall (any material) at the offset; with `index`, its autotile index is one of these. */
    | { neighbour: WallOffset; index?: number[] }
    /** A tile is already on the definition's layer at the offset (see the module note on order). */
    | { exists: WallOffset }
    | { all: WallCondition[] }
    | { any: WallCondition[] }
    | { not: WallCondition }

/** One alternative of a choice: taken when `when` holds (no `when`: always, so it must be last). */
export interface WallAlternative {
    when?: WallCondition
    emit: WallEmit[]
}

/** The first alternative whose condition holds is emitted; if none holds, nothing is. */
export interface WallChoice {
    choose: WallAlternative[]
}

export type WallStep = WallEmit | WallChoice

/** What goes under every cell of the wall, besides its art. Omitted: nothing on that layer. */
export interface WallCellDecorations {
    /** The collision marker (HouseBuilder's `blocking` layer). */
    blocking?: TileRef
    /** The line-of-sight marker (HouseBuilder's `occlusion` layer). */
    occlusion?: TileRef
}

export interface WallAutotileDefinition {
    id: string
    name: string
    kind: "wall"
    /** The tileset every emitted tile is a position in. */
    tilesetId: string
    /** The layer the art goes on (and the one `exists` asks about). */
    layerId: string
    cell: WallCellDecorations
    /** The item that builds this material (an item with buildType "wall"). Omitted: no item builds it. */
    itemType?: string
    /** Autotile index (1-47) -> the steps that draw it, in order. An index with no rule cannot be drawn. */
    rules: Record<string, WallStep[]>
    /** Why each index draws what it draws, shown by the Lab. */
    notes?: Record<string, string>
    /**
     * On storeys above the ground: a tile the rules emit -> the tile drawn there instead (a face that
     * ends in a floor beam rather than a foundation, say). Tiles not listed look the same on every storey.
     */
    upperTiles?: Record<string, number>
    /**
     * Ornaments: named alternatives for some of the material's pieces (a window in a plain run of
     * wall, a patch of moss). `tiles` maps a tile the rules (or upperTiles) emit to the tile drawn
     * instead when the wall cell carries the ornament. A cell carries one when a player PLACES it (a
     * window item marks a wall cell), or, for an ornament with `scatter`, on that share of cells,
     * picked by a hash of the cell so editing a wall never reshuffles the others.
     */
    ornaments?: Record<string, WallOrnament>
    /**
     * Optional: how this wall meets the storeys above and below it (see WallSeams). Without it, a wall
     * under another storey's wall shows its own top as a ledge, as it always did.
     */
    seams?: WallSeams
    /** Optional: what the material lays along its walls beyond its pieces (masonry, coping, ivy...): see wall_trims.ts. */
    trims?: WallTrims
    /** Optional: how the material casts the sun's shadow when not as wall-stone does (its body, or a free block). */
    shadow?: WallShadow
    /**
     * A parapet stands on the wall of the storey below without covering its top (merlons on a castle wall): that
     * wall keeps its top as drawn, trims and all, where any other wall above would cover it.
     */
    parapet?: boolean
    /**
     * Which walls it joins. By default every wall, whatever its material. "own": only walls of its own material, and
     * other walls are shaped as if it were not there (a railing beside a wall ends at its own post and leaves the
     * wall's corner a corner). See autotiler.ts `wallAutotile`.
     */
    joins?: "own"
    /**
     * A solid block (a stone platform): it fills its cells as a floor does, any shape of them, 2x2 and up -- each cell's
     * top drawn a storey up on the row above it, its face on its own cell where no block stands south of it -- and blocks
     * as a wall does. Its top is the walking surface of the storey above (room/ember/wall_top.ts). It joins only its own
     * kind, must draw every autotile index, and an unfinished storey of it never borrows the shapes of the storey below.
     */
    block?: boolean
}

/** Whether a material is a solid block (see WallAutotileDefinition.block). */
export const isBlockWall = (definition: WallAutotileDefinition | undefined): boolean => definition?.block === true

/** The kind a cell of this material joins only (its own id), when it joins only its own kind. */
export const wallJoinsOnly = (definition: WallAutotileDefinition | undefined): string | undefined => (definition?.joins === "own" ? definition.id : undefined)

/** Whether a wall of this material standing on another covers that wall's top (any wall but a parapet). */
export const coversWallBelow = (definition: WallAutotileDefinition | undefined): boolean => !!definition && !definition.parapet

/**
 * How a wall material casts the sun's shadow (the client's building_shadow_geometry.ts). Positions are cell-local px.
 */
export interface WallShadow {
    /** Where its body stands: a north-south run spans x left..right, an east-west run is `depth` deep on the cell's bottom edge. */
    body?: { left: number; right: number; depth: number }
    /**
     * Each cell a block of its own, joined to no neighbour (a merlon): its footprint x left..right, rows top..bottom as
     * drawn on the cell (its top moved down its height), `height` px tall. Instead of `body`.
     */
    block?: { left: number; right: number; top: number; bottom: number; height: number }
}

/**
 * A wall has a 32px face under a ~16px top. Depending on storey spacing, on the front of a house
 * nothing of the storey above covers that top, so it shows as a ledge between the storeys, and over a
 * doorway the floor behind shows. Where the storey above has a wall on the same cell, the wall's top
 * pieces draw `covered` instead: a beam marking the storey ("beam"), or more of the face, so the
 * storeys run together ("seamless"). `lintel` spans a doorway in a run of this wall that the storey
 * above walls over. For a seamless material, an upper wall standing on this material draws its face
 * pieces as `continued`: without the base (a plinth, joist ends) that would break the facade.
 * Optional `joints` override selected upper faces with an end-grain notch over the same material,
 * for either style. Without them the existing covered/continued behavior is unchanged.
 * Keys are tiles this wall draws (positions in its tileset); the pieces may live in any tileset.
 */
export interface WallSeams {
    style: "beam" | "seamless"
    covered: Record<string, TileRef>
    continued?: Record<string, TileRef>
    /** Optional end-grain joints at the south end of an upper run standing on the same material. */
    joints?: Record<string, TileRef>
    lintel?: TileRef
    /**
     * The beam is the material's own (a composed draft's `beam` swatch: a jetty's bressumer), and the lintel holds it
     * whole, so the Lab can draw the covered tops from it again when it remakes the seams.
     */
    ownBeam?: boolean
}

/**
 * The piece a wall tile draws given its storeys: `role` "top" for a piece on the row above its wall
 * cell (its top), "face" for one on the cell itself; `above` whether the storey above has a wall on
 * that cell; `belowSame` whether the storey below has a wall of this material there; `southWall`
 * whether this storey's run continues south. Internal joins stay continuous. Undefined: the tile as it is.
 */
export const wallSeamPiece = (definition: WallAutotileDefinition | undefined, tilePos: number, role: "top" | "face", above: boolean, belowSame: boolean, southWall = false): TileRef | undefined => {
    const seams = definition?.seams
    if (!seams) return undefined
    if (role === "top" && above) return seams.covered[String(tilePos)]
    if (role === "face" && belowSame) return (!southWall ? seams.joints?.[String(tilePos)] : undefined) ?? (seams.style === "seamless" ? seams.continued?.[String(tilePos)] : undefined)
    return undefined
}

export interface WallOrnament {
    tiles: Record<string, number>
    /** 0-1: the share of wall cells that carry it unprompted. Without it, the ornament is placed only. */
    scatter?: number
}

/** A stable number in [0, 1) for a wall cell, its storey and an ornament's name. */
export const wallOrnamentHash = (x: number, y: number, storey: number, name: string): number => {
    let h = 2166136261 ^ (x * 374761393) ^ (y * 668265263) ^ (storey * 2246822519)
    for (let i = 0; i < name.length; i++) h = Math.imul(h ^ name.charCodeAt(i), 16777619)
    h = Math.imul(h ^ (h >>> 15), 2246822507)
    h = Math.imul(h ^ (h >>> 13), 3266489909)
    return ((h ^ (h >>> 16)) >>> 0) / 4294967296
}

/**
 * The ornament a wall cell draws: the one placed on it, when its material has that ornament;
 * otherwise the first scattered ornament (by name) whose hash falls under its share.
 */
export const wallOrnamentFor = (definition: WallAutotileDefinition | undefined, placed: string | undefined, x: number, y: number, storey = 0): string | undefined => {
    const ornaments = definition?.ornaments
    if (!ornaments) return undefined
    if (placed && ornaments[placed]) return placed
    return Object.keys(ornaments).sort().find(name => (ornaments[name].scatter ?? 0) > 0 && wallOrnamentHash(x, y, storey, name) < ornaments[name].scatter)
}

export const isWallChoice = (step: WallStep): step is WallChoice => (step as WallChoice).choose !== undefined

// ---------------------------------------------------------------------------------------------
// Validation (validateAutotileFile calls this for kind "wall")
// ---------------------------------------------------------------------------------------------

export interface WallValidationHelpers {
    fail: (message: string) => void
    /** Checks a position in the definition's own tileset. */
    tile: (label: string, value: unknown) => void
    /** Checks a tile reference into any tileset. */
    tileRef: (label: string, value: unknown) => void
    layer: (label: string, value: unknown, required: boolean) => void
}

const isInt = (value: unknown): value is number => typeof value === "number" && Number.isInteger(value)
const isIndex = (value: unknown) => isInt(value) && value >= 1 && value <= 47
const isObject = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value)

const EMIT_KEYS = ["dx", "dy", "tile", "parent"]
const ALTERNATIVE_KEYS = ["when", "emit"]
const CONDITION_FORMS = ["neighbour", "exists", "all", "any", "not"]

export const validateWallDefinition = (definition: WallAutotileDefinition, { fail, tile, tileRef, layer }: WallValidationHelpers) => {
    layer("layerId", definition.layerId, true)
    if (definition.itemType !== undefined && (typeof definition.itemType !== "string" || !/^[A-Za-z0-9][A-Za-z0-9_.-]*$/.test(definition.itemType))) {
        fail(`itemType must be an item id, got ${JSON.stringify(definition.itemType)}`)
    }

    if (!isObject(definition.cell)) {
        fail("cell must say what goes under every wall cell ({} for nothing)")
    } else {
        Object.keys(definition.cell).forEach(key => {
            if (key !== "blocking" && key !== "occlusion") fail(`cell: unknown decoration "${key}" (expected blocking, occlusion)`)
            else tileRef(`cell.${key}`, definition.cell[key])
        })
    }

    const offset = (label: string, value: unknown, { allowSelf }: { allowSelf: boolean }) => {
        if (!Array.isArray(value) || value.length !== 2 || !value.every(isInt)) {
            fail(`${label} must be an offset [dx, dy] of integers, got ${JSON.stringify(value)}`)
        } else if (value.some(d => d < -1 || d > 1)) {
            fail(`${label} ${JSON.stringify(value)} is outside the wall's neighbourhood (each of dx, dy is -1, 0 or 1)`)
        } else if (!allowSelf && value[0] === 0 && value[1] === 0) {
            fail(`${label} [0, 0] is the wall's own cell`)
        }
    }

    const emit = (label: string, value: unknown) => {
        if (!isObject(value)) {
            fail(`${label} must be a tile to emit ({ "dx", "dy", "tile", "parent" })`)
            return
        }
        Object.keys(value).forEach(key => {
            if (!EMIT_KEYS.includes(key)) fail(`${label}: unknown field "${key}" (expected ${EMIT_KEYS.join(", ")})`)
        })
        ;(["dx", "dy"] as const).forEach(axis => {
            const d = value[axis]
            if (d === undefined) return
            if (!isInt(d)) fail(`${label}.${axis} must be an integer, got ${JSON.stringify(d)}`)
            else if (d < -1 || d > 1) fail(`${label}.${axis} ${d} is outside the wall's neighbourhood (-1, 0 or 1)`)
        })
        tile(`${label}.tile`, value.tile)
        if (value.parent !== undefined && value.parent !== "self") fail(`${label}.parent must be "self" or left out, got ${JSON.stringify(value.parent)}`)
    }

    const condition = (label: string, value: unknown, depth = 0) => {
        if (depth > 8) {
            fail(`${label}: conditions nest too deep`)
            return
        }
        if (!isObject(value)) {
            fail(`${label} must be a condition (one of ${CONDITION_FORMS.join(", ")})`)
            return
        }
        const forms = Object.keys(value).filter(key => CONDITION_FORMS.includes(key))
        const unknown = Object.keys(value).filter(key => !CONDITION_FORMS.includes(key) && !(key === "index" && forms[0] === "neighbour"))
        if (forms.length !== 1) {
            fail(`${label}: unknown condition form ${JSON.stringify(Object.keys(value))} (expected exactly one of ${CONDITION_FORMS.join(", ")})`)
            return
        }
        unknown.forEach(key => fail(`${label}: unknown field "${key}" in a ${forms[0]} condition`))
        switch (forms[0]) {
            case "neighbour":
                offset(`${label}.neighbour`, value.neighbour, { allowSelf: false })
                if (value.index !== undefined) {
                    if (!Array.isArray(value.index) || value.index.length === 0) fail(`${label}.index must be a non-empty list of autotile indices`)
                    else value.index.forEach((i, n) => !isIndex(i) && fail(`${label}.index[${n}] ${JSON.stringify(i)} is not an autotile index (1-47)`))
                }
                break
            case "exists":
                offset(`${label}.exists`, value.exists, { allowSelf: true })
                break
            case "all":
            case "any": {
                const parts = value[forms[0]]
                if (!Array.isArray(parts) || parts.length === 0) fail(`${label}.${forms[0]} must be a non-empty list of conditions`)
                else parts.forEach((part, n) => condition(`${label}.${forms[0]}[${n}]`, part, depth + 1))
                break
            }
            case "not":
                condition(`${label}.not`, value.not, depth + 1)
                break
        }
    }

    if (!isObject(definition.rules)) {
        fail("rules must map autotile indices to lists of steps")
    } else {
        if (Object.keys(definition.rules).length === 0) fail("rules is empty: the material could draw nothing")
        Object.entries(definition.rules).forEach(([index, steps]) => {
            const where = `rules[${index}]`
            if (!/^\d+$/.test(index) || !isIndex(Number(index))) fail(`rules: "${index}" is not an autotile index (1-47)`)
            if (!Array.isArray(steps) || steps.length === 0) {
                fail(`${where} must be a non-empty list of steps (leave the index out if the material cannot draw it)`)
                return
            }
            steps.forEach((step, s) => {
                const at = `${where}[${s}]`
                if (isObject(step) && "choose" in step) {
                    Object.keys(step).forEach(key => key !== "choose" && fail(`${at}: unknown field "${key}" beside "choose"`))
                    const alternatives = step.choose
                    if (!Array.isArray(alternatives) || alternatives.length === 0) {
                        fail(`${at}.choose must be a non-empty list of alternatives`)
                        return
                    }
                    alternatives.forEach((alternative, a) => {
                        const alt = `${at}.choose[${a}]`
                        if (!isObject(alternative)) {
                            fail(`${alt} must be an alternative ({ "when", "emit" })`)
                            return
                        }
                        Object.keys(alternative).forEach(key => {
                            if (!ALTERNATIVE_KEYS.includes(key)) fail(`${alt}: unknown field "${key}" (expected when, emit)`)
                        })
                        if (alternative.when === undefined) {
                            if (a !== alternatives.length - 1) fail(`${alt} has no "when", so the alternatives after it can never be taken`)
                        } else {
                            condition(`${alt}.when`, alternative.when)
                        }
                        if (!Array.isArray(alternative.emit)) fail(`${alt}.emit must be a list of tiles ([] for none)`)
                        else alternative.emit.forEach((e, n) => emit(`${alt}.emit[${n}]`, e))
                    })
                } else {
                    emit(at, step)
                }
            })
        })
    }

    if (definition.notes !== undefined) {
        if (!isObject(definition.notes)) fail("notes must map autotile indices to strings")
        else Object.entries(definition.notes).forEach(([index, note]) => {
            if (!isIndex(Number(index)) || typeof note !== "string") fail(`notes: "${index}" must be an index with a string note`)
        })
    }

    if (definition.ornaments !== undefined) {
        if (!isObject(definition.ornaments)) fail("ornaments must map ornament names to { tiles, scatter? }")
        else {
            const drawn = new Set<string>(Object.values(isObject(definition.upperTiles) ? definition.upperTiles : {}).map(String))
            Object.values(isObject(definition.rules) ? definition.rules : {}).forEach(steps => Array.isArray(steps) && steps.forEach(step =>
                (isWallChoice(step) ? (step.choose || []).flatMap(a => a.emit || []) : [step]).forEach(e => isObject(e) && drawn.add(String(e.tile)))))
            Object.entries(definition.ornaments).forEach(([name, ornament]) => {
                const at = `ornaments.${name}`
                if (!/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/.test(name)) fail(`${at}: an ornament's name is lower-case words joined by '-'`)
                if (!isObject(ornament) || !isObject(ornament.tiles) || !Object.keys(ornament.tiles).length) {
                    fail(`${at} must give the tiles it replaces ({ tiles: { emitted: replacement } })`)
                    return
                }
                Object.entries(ornament.tiles).forEach(([from, to]) => {
                    if (!drawn.has(from)) fail(`${at}.tiles: "${from}" is not a tile the rules or upperTiles draw`)
                    tile(`${at}.tiles[${from}]`, to)
                })
                if (ornament.scatter !== undefined && !(typeof ornament.scatter === "number" && ornament.scatter > 0 && ornament.scatter <= 1)) fail(`${at}.scatter must be a share between 0 and 1`)
                Object.keys(ornament).forEach(key => key !== "tiles" && key !== "scatter" && fail(`${at}: unknown field "${key}" (expected tiles, scatter)`))
            })
        }
    }

    if (definition.seams !== undefined) {
        const seams = definition.seams as unknown as Record<string, unknown>
        if (!isObject(seams)) fail("seams must be { style, covered, continued?, joints?, lintel?, ownBeam? }")
        else {
            if (seams.style !== "beam" && seams.style !== "seamless") fail(`seams.style must be "beam" or "seamless", got ${JSON.stringify(seams.style)}`)
            const pieces = (label: string, map: unknown, required: boolean) => {
                if (map === undefined && !required) return
                if (!isObject(map)) { fail(`${label} must map tiles this wall draws to tile references`); return }
                Object.entries(map).forEach(([from, to]) => {
                    if (!/^\d+$/.test(from)) fail(`${label}: "${from}" is not a tile position`)
                    else tile(`${label} key ${from}`, Number(from))
                    tileRef(`${label}[${from}]`, to)
                })
            }
            pieces("seams.covered", seams.covered, true)
            pieces("seams.continued", seams.continued, false)
            pieces("seams.joints", seams.joints, false)
            if (seams.lintel !== undefined) tileRef("seams.lintel", seams.lintel)
            if (seams.ownBeam !== undefined && (seams.ownBeam !== true || seams.style !== "beam" || seams.lintel === undefined)) fail("seams.ownBeam must be true, on a beam with a lintel (which holds the beam)")
            Object.keys(seams).forEach(key => !["style", "covered", "continued", "joints", "lintel", "ownBeam"].includes(key) && fail(`seams: unknown field "${key}" (expected style, covered, continued, joints, lintel, ownBeam)`))
        }
    }

    if (definition.trims !== undefined) validateWallTrims(definition.trims, fail)
    if (definition.shadow !== undefined) validateWallShadow(definition.shadow, fail)
    if (definition.parapet !== undefined && typeof definition.parapet !== "boolean") fail("parapet must be true or false")
    if (definition.joins !== undefined && definition.joins !== "own") fail('joins must be "own" (or left out: it joins every wall)')
    if (definition.block !== undefined) {
        if (typeof definition.block !== "boolean") fail("block must be true or false")
        else if (definition.block) {
            if (definition.joins !== "own") fail('a block joins only its own kind: give it joins "own"')
            const missing = Array.from({ length: 47 }, (_, i) => i + 1).filter(index => !isObject(definition.rules) || !definition.rules[String(index)])
            if (missing.length) fail(`a block fills any shape of cells, so it must draw every autotile index; missing ${missing.join(", ")}`)
            if (definition.parapet) fail("a block cannot be a parapet: it covers what it stands on")
        }
    }

    if (definition.upperTiles !== undefined) {
        if (!isObject(definition.upperTiles)) fail("upperTiles must map tiles the rules emit to the tiles drawn above the ground")
        else {
            const emitted = new Set<string>()
            Object.values(isObject(definition.rules) ? definition.rules : {}).forEach(steps => Array.isArray(steps) && steps.forEach(step =>
                (isWallChoice(step) ? (step.choose || []).flatMap(a => a.emit || []) : [step]).forEach(e => isObject(e) && emitted.add(String(e.tile)))))
            Object.entries(definition.upperTiles).forEach(([from, to]) => {
                if (!emitted.has(from)) fail(`upperTiles: "${from}" is not a tile the rules emit`)
                tile(`upperTiles[${from}]`, to)
            })
        }
    }
}

/** Checks a definition's shadow profile, reporting through `fail`. */
export const validateWallShadow = (shadow: unknown, fail: (message: string) => void) => {
    const isObject = (v: unknown): v is Record<string, any> => !!v && typeof v === "object" && !Array.isArray(v)
    const local = (n: unknown) => Number.isInteger(n) && (n as number) >= 0 && (n as number) <= 32
    const footprint = (v: unknown) => isObject(v) && local(v.left) && local(v.right) && local(v.depth) && v.left < v.right && v.depth > 0
    if (!isObject(shadow)) return fail("shadow must be { body? } or { block }")
    Object.keys(shadow).forEach(key => !["body", "block"].includes(key) && fail(`shadow: unknown field "${key}" (expected body, block)`))
    if (shadow.body !== undefined && !footprint(shadow.body)) fail("shadow.body must be { left, right, depth } in cell-local px, left < right")
    const block = shadow.block
    if (block !== undefined && !(isObject(block) && [block.left, block.right, block.top, block.bottom].every(local) && block.left < block.right &&
        block.top < block.bottom && Number.isInteger(block.height) && block.height > 0 && block.height <= 64)) {
        fail("shadow.block must be { left, right, top, bottom, height } in cell-local px, left < right, top < bottom, height 1-64")
    }
    if (shadow.body !== undefined && shadow.block !== undefined) fail("shadow: a block casts instead of a body; give one")
}

// ---------------------------------------------------------------------------------------------
// Trims: what a material lays along its walls beyond its pieces (drawn by room/wall_trims.ts)
// ---------------------------------------------------------------------------------------------

export interface WallTrimMasonry {
    /** The face rows the stones fill, inclusive (cell-local). */
    rows: [number, number]
    /** The widths a column of stones may take, px, its joint included. */
    widths: number[]
    /** How a column splits into stones, top to bottom: heights px, each joint included; the last stone takes what is left. */
    splits: number[][]
}

export interface WallTrimPalette {
    outline: string
    joint: string
    /** Stone, darkest to lightest (5). Masonry, rubble and spurs. Keep these among `parts.face` so ivy climbs the stones. */
    stone: string[]
    /** A top's stone: joint, shade, mid, light, rim (5). Uneven coping. */
    top: string[]
    /** Dressed stone: joint, shade, mid, light (4). An arch's voussoirs, a spur's cap. */
    dressed: string[]
    /** Darkest to lightest. */
    moss: string[]
    grass: string[]
    leaves: string[]
    stem: string
    /** No longer drawn: an arch's opening shows the passage, shaded by the sun's shadows. Accepted so older content validates. */
    vault?: string
    /** Timber: dark, shade, mid, light, highlight (5). The posts, studs and braces of `framing`. */
    timber?: string[]
    /** A creeper's blossoms: dark, mid, light (3). With `creepers.bloom`. */
    flowers?: string[]
}

/** What fills one panel of a timber frame between its posts and rails. */
export type WallTrimFill =
    /** bare infill */
    | "plain"
    /** one stud up its middle */
    | "stud"
    /** close studding: studs every 7 or 8 px */
    | "studs"
    /** one straight brace rising from a post to the head of the panel */
    | "brace"
    /** a pair of braces rising from both posts to meet at the head's middle */
    | "chevron"
    /** a saltire, corner to corner */
    | "cross"
    /** curved braces filling both head corners, so the panel's head is an arch */
    | "arched"

export const WALL_TRIM_FILLS: WallTrimFill[] = ["plain", "stud", "studs", "brace", "chevron", "cross", "arched"]

export interface WallTrimFramingPanel {
    /** Its rows, inclusive (cell-local face rows). */
    rows: [number, number]
    /** The fills a panel of these rows may take, weighted. A fill a panel is too narrow for is passed over. */
    fills: Partial<Record<WallTrimFill, number>>
}

/**
 * Timber framing laid over a face of infill (daub, plaster, brick nogging): posts by world position, the panels
 * between them and the rails filled with studs, braces, saltires, arched heads. Members are laid only over the face
 * (and `over`), never over other art (a window and its frame, a corner post, a doorway), and a post or a fill that
 * would touch other art is left out, so ornaments keep their own framing.
 */
export interface WallTrimFraming {
    /** The rows posts run, inclusive: from under the head plate to the sill, through the rails between the panels. */
    posts: [number, number]
    /** Post spacing px, centre to centre, laid from world x 0 by a hash: the same on every storey, so posts stand over posts. */
    bays: number[]
    /** A post's width px; studs and braces are one narrower. */
    width: number
    panels: WallTrimFramingPanel[]
    /** Colours (#rrggbb) members may be laid over besides the face's: wattle where the daub has fallen away. */
    over?: string[]
    /** Each panel's infill a little lighter or darker than its neighbours (a share, 0-0.12): limewashed at different times. */
    tones?: number
}

export interface WallTrims {
    /** Which colours of the material's art are which part (#rrggbb): trims find a piece's face, top and coping by them. */
    parts: { face: string[]; top: string[]; coping?: string[] }
    palette: WallTrimPalette
    /** Stones laid along each storey's face; `upper` for storeys above the ground (default: `ground`). */
    masonry?: { ground: WallTrimMasonry; upper?: WallTrimMasonry }
    /** Uneven coping along the back edge of a top nothing stands on: stone lengths px. */
    coping?: { lengths: [number, number] }
    /** At the foot of a ground-storey face: the share of spots that get rubble, a tuft, a patch of moss. */
    foot?: { rubble: number; tufts: number; moss: number }
    /** A battered spur at each outer corner of the ground storey: how far it reaches out at its foot, how tall it stands, px. */
    spurs?: { reach: number; height: number }
    /** Water streaks under a top nothing stands on: the share of pixel columns. */
    streaks?: { share: number }
    /** Tufts along an uncovered top's back edge: the share of cells. */
    weeds?: { share: number }
    /** A gateway (a gap of at most `maxSpan` cells) walled over by the storey above becomes an arch rising `rise` px into it. */
    arch?: { rise: number; ring: number; maxSpan: number }
    /**
     * Ivy: the share of ground-storey faces on soil that root one, how far it climbs (storeys), how wide it spreads (px);
     * `bloom`, the share of its leaf clusters that flower (a climbing rose; palette.flowers).
     */
    creepers?: { share: number; reach: [number, number]; spread: number; bloom?: number }
    /** Timber framing over the face (WallTrimFraming): posts, studs, braces. Needs palette.timber. */
    framing?: WallTrimFraming
    /**
     * A smooth face (render, plaster) runs on through its storeys: where a storey's wall stands on this material, the
     * line at its foot is painted as the face above it, so the storeys read as one wall with no line between them.
     */
    continuous?: boolean
    /** Rising damp: a darker band at the foot of a ground-storey face, its wavering tide mark `height` px [least, most] up. */
    damp?: { height: [number, number] }
    /**
     * Quoins: dressed stones laid up every outer end of a face (a corner, a jamb), long and short in turn, so a
     * rendered or rubble wall is framed in dressed stone. `long` and `short` px into the face, `course` px tall.
     */
    quoins?: { long: number; short: number; course: number }
    /**
     * A string course: a band of dressed stone along the foot of every `every`th storey that stands on this material,
     * dividing a tall face into stages, with a line of shade under it on the storey below.
     */
    stringCourse?: { every: number }
}

const HEX = /^#[0-9a-fA-F]{6}$/

/** Checks a definition's trims, reporting through `fail`. */
export const validateWallTrims = (trims: unknown, fail: (message: string) => void) => {
    const isObject = (v: unknown): v is Record<string, any> => !!v && typeof v === "object" && !Array.isArray(v)
    if (!isObject(trims)) return fail("trims must be an object")
    const colours = (label: string, value: unknown, length?: number) => {
        if (!Array.isArray(value) || !value.length || value.some(c => typeof c !== "string" || !HEX.test(c))) return fail(`${label} must be a list of #rrggbb colours`)
        if (length !== undefined && value.length !== length) fail(`${label} must have ${length} colours`)
    }
    const share = (label: string, value: unknown) => {
        if (typeof value !== "number" || !(value >= 0 && value <= 1)) fail(`${label} must be a share between 0 and 1`)
    }
    const px = (label: string, value: unknown, min = 0, max = 256) => {
        if (!Number.isInteger(value) || (value as number) < min || (value as number) > max) fail(`${label} must be a whole number of pixels ${min}-${max}`)
    }
    const known = ["parts", "palette", "masonry", "coping", "foot", "spurs", "streaks", "weeds", "arch", "creepers", "continuous", "damp", "quoins", "stringCourse", "framing"]
    Object.keys(trims).forEach(key => !known.includes(key) && fail(`trims: unknown field "${key}" (expected ${known.join(", ")})`))
    if (!isObject(trims.parts)) fail("trims.parts must be { face, top, coping? }")
    else {
        colours("trims.parts.face", trims.parts.face)
        colours("trims.parts.top", trims.parts.top)
        if (trims.parts.coping !== undefined) colours("trims.parts.coping", trims.parts.coping)
    }
    const p = trims.palette
    if (!isObject(p)) fail("trims.palette must be an object")
    else {
        ;["outline", "joint", "stem"].forEach(key => (typeof p[key] !== "string" || !HEX.test(p[key])) && fail(`trims.palette.${key} must be a #rrggbb colour`))
        if (p.vault !== undefined && (typeof p.vault !== "string" || !HEX.test(p.vault))) fail("trims.palette.vault must be a #rrggbb colour")
        colours("trims.palette.stone", p.stone, 5)
        colours("trims.palette.top", p.top, 5)
        colours("trims.palette.dressed", p.dressed, 4)
        colours("trims.palette.moss", p.moss)
        colours("trims.palette.grass", p.grass)
        colours("trims.palette.leaves", p.leaves)
        if (p.timber !== undefined) colours("trims.palette.timber", p.timber, 5)
        if (p.flowers !== undefined) colours("trims.palette.flowers", p.flowers, 3)
    }
    const masonry = (label: string, m: unknown) => {
        if (!isObject(m)) return fail(`${label} must be { rows, widths, splits }`)
        if (!Array.isArray(m.rows) || m.rows.length !== 2 || !m.rows.every((r: unknown) => Number.isInteger(r) && (r as number) >= 0 && (r as number) < 32) || m.rows[0] > m.rows[1]) fail(`${label}.rows must be [first, last] face rows 0-31`)
        if (!Array.isArray(m.widths) || !m.widths.length || m.widths.some((w: unknown) => !Number.isInteger(w) || (w as number) < 4 || (w as number) > 96)) fail(`${label}.widths must be widths of 4-96 px`)
        if (!Array.isArray(m.splits) || !m.splits.length || m.splits.some((s: unknown) => !Array.isArray(s) || !s.length || s.some(h => !Number.isInteger(h) || h < 3 || h > 32))) fail(`${label}.splits must be lists of stone heights of 3-32 px`)
    }
    if (trims.masonry !== undefined) {
        if (!isObject(trims.masonry)) fail("trims.masonry must be { ground, upper? }")
        else {
            masonry("trims.masonry.ground", trims.masonry.ground)
            if (trims.masonry.upper !== undefined) masonry("trims.masonry.upper", trims.masonry.upper)
        }
    }
    if (trims.coping !== undefined) {
        const l = trims.coping?.lengths
        if (!Array.isArray(l) || l.length !== 2 || !l.every((n: unknown) => Number.isInteger(n) && (n as number) >= 3 && (n as number) <= 64) || l[0] > l[1]) fail("trims.coping.lengths must be [shortest, longest] px, 3-64")
    }
    if (trims.foot !== undefined) ["rubble", "tufts", "moss"].forEach(key => share(`trims.foot.${key}`, trims.foot?.[key]))
    if (trims.spurs !== undefined) {
        px("trims.spurs.reach", trims.spurs?.reach, 2, 16)
        px("trims.spurs.height", trims.spurs?.height, 4, 31)
    }
    if (trims.streaks !== undefined) share("trims.streaks.share", trims.streaks?.share)
    if (trims.weeds !== undefined) share("trims.weeds.share", trims.weeds?.share)
    if (trims.arch !== undefined) {
        px("trims.arch.rise", trims.arch?.rise, 4, 30)
        px("trims.arch.ring", trims.arch?.ring, 2, 12)
        px("trims.arch.maxSpan", trims.arch?.maxSpan, 1, 4)
    }
    if (trims.creepers !== undefined) {
        share("trims.creepers.share", trims.creepers?.share)
        const r = trims.creepers?.reach
        if (!Array.isArray(r) || r.length !== 2 || !r.every((n: unknown) => typeof n === "number" && n > 0 && n <= 12) || r[0] > r[1]) fail("trims.creepers.reach must be [least, most] storeys")
        px("trims.creepers.spread", trims.creepers?.spread, 2, 64)
        if (trims.creepers?.bloom !== undefined) {
            share("trims.creepers.bloom", trims.creepers.bloom)
            if (!isObject(p) || p.flowers === undefined) fail("trims.creepers.bloom needs trims.palette.flowers")
        }
    }
    if (trims.framing !== undefined) {
        const f = trims.framing
        const rows = (label: string, r: unknown) => {
            if (!Array.isArray(r) || r.length !== 2 || !r.every(n => Number.isInteger(n) && n >= 0 && n < 32) || r[0] > r[1]) fail(`${label} must be [first, last] face rows 0-31`)
        }
        if (!isObject(f)) fail("trims.framing must be { posts, bays, width, panels, over?, tones? }")
        else {
            const stray = Object.keys(f).filter(key => !["posts", "bays", "width", "panels", "over", "tones"].includes(key))
            if (stray.length) fail(`trims.framing: unknown field ${stray.join(", ")}`)
            rows("trims.framing.posts", f.posts)
            if (!Array.isArray(f.bays) || !f.bays.length || f.bays.some((w: unknown) => !Number.isInteger(w) || (w as number) < 12 || (w as number) > 64)) fail("trims.framing.bays must be post spacings of 12-64 px")
            px("trims.framing.width", f.width, 3, 6)
            if (!Array.isArray(f.panels) || !f.panels.length) fail("trims.framing.panels must be a list of { rows, fills }")
            else f.panels.forEach((panel: unknown, i: number) => {
                if (!isObject(panel)) return fail(`trims.framing.panels[${i}] must be { rows, fills }`)
                rows(`trims.framing.panels[${i}].rows`, panel.rows)
                const fills = panel.fills
                if (!isObject(fills) || !Object.keys(fills).length) return fail(`trims.framing.panels[${i}].fills must name some fills`)
                Object.entries(fills).forEach(([name, weight]) => {
                    if (!WALL_TRIM_FILLS.includes(name as WallTrimFill)) fail(`trims.framing.panels[${i}].fills: unknown fill "${name}" (expected ${WALL_TRIM_FILLS.join(", ")})`)
                    if (typeof weight !== "number" || !(weight >= 0)) fail(`trims.framing.panels[${i}].fills.${name} must be a weight of 0 or more`)
                })
            })
            if (f.over !== undefined) colours("trims.framing.over", f.over)
            if (f.tones !== undefined && (typeof f.tones !== "number" || !(f.tones >= 0 && f.tones <= 0.12))) fail("trims.framing.tones must be a share between 0 and 0.12")
            if (!isObject(p) || p.timber === undefined) fail("trims.framing needs trims.palette.timber")
        }
    }
    if (trims.continuous !== undefined && typeof trims.continuous !== "boolean") fail("trims.continuous must be true or false")
    if (trims.damp !== undefined) {
        const h = trims.damp?.height
        if (!Array.isArray(h) || h.length !== 2 || !h.every((n: unknown) => Number.isInteger(n) && (n as number) >= 1 && (n as number) <= 24) || h[0] > h[1]) fail("trims.damp.height must be [least, most] px, 1-24")
    }
    if (trims.quoins !== undefined) {
        px("trims.quoins.long", trims.quoins?.long, 3, 24)
        px("trims.quoins.short", trims.quoins?.short, 2, 24)
        px("trims.quoins.course", trims.quoins?.course, 3, 32)
        if (trims.quoins?.short > trims.quoins?.long) fail("trims.quoins.short must be at most trims.quoins.long")
    }
    if (trims.stringCourse !== undefined) {
        const every = trims.stringCourse?.every
        if (!Number.isInteger(every) || every < 1 || every > 12) fail("trims.stringCourse.every must be a whole number of storeys, 1-12")
    }
}

// ---------------------------------------------------------------------------------------------
// Resolution: what one wall cell emits
// ---------------------------------------------------------------------------------------------

/** One tile a wall cell writes, in the order it writes them. */
export interface WallWrite {
    /** The tile's own cell. */
    x: number
    y: number
    dx: number
    dy: number
    layerId: string
    tilesetId: string
    tilePos: number
    /** The cell the tile is grouped under. */
    parent: { x: number; y: number }
    parentMode: "anchor" | "self"
    /** The rule step that wrote it, and inside a choice the alternative taken. */
    step: number
    alternative?: number
}

/** A condition with its verdict, for the Lab's inspector. */
export interface WallConditionTrace {
    text: string
    holds: boolean
    parts?: WallConditionTrace[]
}

export interface WallChoiceTrace {
    step: number
    /** The alternative taken, or undefined when none held. */
    chosen?: number
    alternatives: { when?: WallConditionTrace; holds: boolean; emits: number }[]
}

export interface WallCellResolution {
    x: number
    y: number
    index: number
    /** The definition has a rule for this index. */
    drawable: boolean
    writes: WallWrite[]
    /** Every choice the rule made. Conditions are described only when asked for (`trace`). */
    choices: WallChoiceTrace[]
}

export interface WallCellContext {
    /** Autotile index of every wall cell, [y][x], all materials together (autotiler.ts `autotile`). */
    indices: ArrayLike<ArrayLike<number>>
    /** Whether a tile is already on `layerId` at (x, y). HouseBuilder: its entry cache. */
    exists: (x: number, y: number, layerId: string) => boolean
}

export interface WallResolveOptions {
    /** Describe every condition evaluated (the Lab). Off for the game. */
    trace?: boolean
    /** The storey the wall stands on (0 the ground): above it, `upperTiles` replace the tiles they name. */
    storey?: number
    /** The ornament the cell carries (wallOrnamentFor): its tiles replace the ones they name, last. */
    ornament?: string
}

const signed = (d: number) => (d > 0 ? `+${d}` : `${d}`)
const offsetText = ([dx, dy]: WallOffset) => `(${signed(dx)}, ${signed(dy)})`

/** The rule a definition has for an index, if any. */
export const wallRule = (definition: WallAutotileDefinition, index: number): WallStep[] | undefined => definition.rules[String(index)]

/** The indices a definition can draw, ascending: exactly the ones it has a rule for. */
export const wallRuleIndices = (definition: WallAutotileDefinition): number[] =>
    Object.keys(definition.rules)
        .map(Number)
        .sort((a, b) => a - b)

export const resolveWallCell = (
    definition: WallAutotileDefinition,
    x: number,
    y: number,
    context: WallCellContext,
    options: WallResolveOptions = {},
): WallCellResolution => {
    const index = context.indices[y]?.[x] ?? 0
    const rule = wallRule(definition, index)
    const writes: WallWrite[] = []
    const choices: WallChoiceTrace[] = []
    if (!rule) return { x, y, index, drawable: false, writes, choices }

    const { layerId, tilesetId } = definition
    // Tiles this cell has already written count as existing, exactly as they would in HouseBuilder's
    // cache had each write been applied as it was made.
    const written = new Set<string>()
    const exists = (cx: number, cy: number) => written.has(`${cx},${cy}`) || context.exists(cx, cy, layerId)
    const indexAt = (cx: number, cy: number) => context.indices[cy]?.[cx] ?? 0

    const evaluate = (condition: WallCondition): WallConditionTrace => {
        const describe = options.trace
        if ("neighbour" in condition) {
            const [dx, dy] = condition.neighbour
            const there = indexAt(x + dx, y + dy)
            const holds = condition.index ? condition.index.includes(there) : !!there
            const text = describe
                ? condition.index
                    ? `neighbour ${offsetText(condition.neighbour)} has index ${there || "none"}; wanted ${condition.index.join(" or ")}`
                    : `a wall at ${offsetText(condition.neighbour)}: ${there ? `yes (index ${there})` : "no"}`
                : ""
            return { text, holds }
        }
        if ("exists" in condition) {
            const [dx, dy] = condition.exists
            const holds = exists(x + dx, y + dy)
            return { text: describe ? `a ${layerId} tile already at ${offsetText(condition.exists)}: ${holds ? "yes" : "no"}` : "", holds }
        }
        if ("not" in condition) {
            const inner = evaluate(condition.not)
            return { text: describe ? "not" : "", holds: !inner.holds, parts: [inner] }
        }
        const all = "all" in condition
        const parts = (all ? (condition as { all: WallCondition[] }).all : (condition as { any: WallCondition[] }).any).map(evaluate)
        return { text: describe ? (all ? "all of" : "any of") : "", holds: all ? parts.every(p => p.holds) : parts.some(p => p.holds), parts }
    }

    const upper = (options.storey ?? 0) > 0 ? definition.upperTiles : undefined
    const ornament = options.ornament ? definition.ornaments?.[options.ornament]?.tiles : undefined
    const drawnTile = (tile: number) => {
        const storeyed = upper && upper[String(tile)] !== undefined ? upper[String(tile)] : tile
        return ornament && ornament[String(storeyed)] !== undefined ? ornament[String(storeyed)] : storeyed
    }
    const emit = (e: WallEmit, step: number, alternative?: number) => {
        const dx = e.dx || 0
        const dy = e.dy || 0
        const self = e.parent === "self"
        writes.push({
            x: x + dx,
            y: y + dy,
            dx,
            dy,
            layerId,
            tilesetId,
            tilePos: drawnTile(e.tile),
            parent: self ? { x: x + dx, y: y + dy } : { x, y },
            parentMode: self ? "self" : "anchor",
            step,
            ...(alternative === undefined ? {} : { alternative }),
        })
        written.add(`${x + dx},${y + dy}`)
    }

    rule.forEach((step, s) => {
        if (!isWallChoice(step)) {
            emit(step, s)
            return
        }
        const trace: WallChoiceTrace = { step: s, alternatives: [] }
        step.choose.forEach((alternative, a) => {
            // Conditions only read, so describing the ones after the chosen alternative changes nothing.
            if (trace.chosen !== undefined && !options.trace) return
            const verdict = alternative.when ? evaluate(alternative.when) : undefined
            const holds = verdict ? verdict.holds : true
            trace.alternatives.push({ ...(verdict && options.trace ? { when: verdict } : {}), holds, emits: alternative.emit.length })
            if (holds && trace.chosen === undefined) trace.chosen = a
        })
        choices.push(trace)
        if (trace.chosen !== undefined) step.choose[trace.chosen].emit.forEach(e => emit(e, s, trace.chosen))
    })
    return { x, y, index, drawable: true, writes, choices }
}

/** The four BuilderUtils a wall needs, as HouseBuilder hands them out (see BuilderUtils in house.ts). */
export interface WallBuilderUtils<E> {
    entry: (x: number, y: number, tilePos: number, layerId: string, tilesetId: string) => E
    addFor: (x: number, y: number, entry: E) => void
    exists: (x: number, y: number, layerId: string) => boolean
}

/**
 * Resolve one wall cell and write it: entry() each tile, addFor() it under its parent, in order.
 * HouseBuilder and Sprite Lab both run walls through this.
 */
export const applyWallCell = <E>(
    definition: WallAutotileDefinition,
    indices: ArrayLike<ArrayLike<number>>,
    x: number,
    y: number,
    utils: WallBuilderUtils<E>,
    options?: WallResolveOptions,
): WallCellResolution => {
    const resolution = resolveWallCell(definition, x, y, { indices, exists: utils.exists }, options)
    resolution.writes.forEach(w => utils.addFor(w.parent.x, w.parent.y, utils.entry(w.x, w.y, w.tilePos, w.layerId, w.tilesetId)))
    return resolution
}
